import { buildPanelMentions, MENTION_TRIGGER, type PanelMention } from '@server/panel-mentions';

export { buildPanelMentions, MENTION_TRIGGER, type PanelMention };

/**
 * Reading mentions back out of a message.
 *
 * The label rule itself lives on the server (`@server/panel-mentions`), because the agent's turn
 * envelope has to print the same label the composer wrote. What is left here is the text side: find
 * the mentions in a sentence, mark them up for the transcript, and splice a new one in.
 */

/** True when nothing precedes the index, or the character before it is whitespace. */
function startsWord(text: string, index: number): boolean {
  if (index === 0) return true;
  return /\s/.test(text.charAt(index - 1));
}

/**
 * True when the match ends a word.
 *
 * Guarding only the left edge made "@Revenues are flat" match the panel "Revenue" and orphan the
 * "s". Punctuation still ends a word, so "@Revenue's" and "@Revenue." both match.
 */
function endsWord(text: string, index: number): boolean {
  if (index >= text.length) return true;
  return !/[\p{L}\p{N}_]/u.test(text.charAt(index));
}

/**
 * Every mention in the text, in the order it appears, with the span it occupies.
 *
 * The message the user sends is the source of truth, not a list held beside it: a mention the user
 * deleted or edited is simply no longer found, and no bookkeeping can drift out of step with the
 * words. Labels are matched longest-first so "@Revenue by Region" wins over "@Revenue", and a mention
 * must start a word so an email address in the text is never read as one.
 *
 * Two callers remain, and both are conversions rather than resolution: the transcript marks up a
 * stored message, and the composer rebuilds nodes from a restored draft. The message a user is
 * writing needs none of this, because its mentions are nodes that carry their own ids.
 */
export function findPanelMentions(
  text: string,
  mentions: PanelMention[],
): { start: number; end: number; mention: PanelMention }[] {
  const byLongestLabel = [...mentions].sort((a, b) => b.label.length - a.label.length);
  const found: { start: number; end: number; mention: PanelMention }[] = [];

  for (let i = text.indexOf(MENTION_TRIGGER); i !== -1; i = text.indexOf(MENTION_TRIGGER, i + 1)) {
    if (!startsWord(text, i)) continue;
    const match = byLongestLabel.find((mention) => text.startsWith(mention.label, i + 1));
    if (!match) continue;
    const end = i + 1 + match.label.length;
    if (!endsWord(text, end)) continue;
    found.push({ start: i, end, mention: match });
    // Skip the matched label so a title containing "@" cannot be read as a second mention.
    i = end - 1;
  }

  return found;
}

/**
 * Wraps each mention in backticks so the transcript renders it as inline code.
 *
 * The bubble already renders markdown, so this reuses the app's own inline-code styling instead of
 * introducing a second one. A label holding a backtick is left alone — wrapping it would end the code
 * span early and mangle the rest of the sentence.
 */
export function markPanelMentions(text: string, mentions: PanelMention[]): string {
  const spans = findPanelMentions(text, mentions).filter(({ mention }) => !mention.label.includes('`'));
  if (spans.length === 0) return text;

  let marked = '';
  let cursor = 0;
  for (const { start, end } of spans) {
    marked += `${text.slice(cursor, start)}\`${text.slice(start, end)}\``;
    cursor = end;
  }
  return marked + text.slice(cursor);
}
